import { ComponentPropsWithoutRef, useState } from "react";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import ConfirmModal from "components/confirm-modal";
import RadioButton from "components/radio-button";
import confirmSessionAttendance from "services/online-offline/confirmSessionAttendance";
import styles from "./ConfirmCancelModal.module.scss";
import { AmplitudePageName, track } from "utils/amplitude";
import { closeSidesheet } from "store/calendarSidebarSlice";
import { useDispatch } from "react-redux";

const CANCEL_REASON_RADIOS = [
  { id: "no-show", label: "No-show", value: "patient_no_show" },
  {
    id: "i-missed-session",
    label: "I missed the session (No charge on patient)",
    value: "abandoned",
  },
];

const AMPLITUDE_COMPONENT_NAME = "confirm-cancel-modal";

type Props = {
  appointmentIds: string[];
  cancelAll: boolean;
  appointmentPrice?: number | string;
  amplitudePageName: AmplitudePageName;
} & Pick<ComponentPropsWithoutRef<typeof ModalLayout>, "show" | "onHide">;

const ConfirmCancelModal = ({
  appointmentIds,
  show,
  onHide,
  cancelAll,
  appointmentPrice,
  amplitudePageName,
}: Props) => {
  const CHARGE_NO_SHOW_RADIOS = [
    {
      id: "late-cancellation-fee",
      label: "Charge cancellation fee",
      value: "charge_late_cancellation_fee",
    },
    {
      id: "charge-session-full-price",
      label: `${
        cancelAll || !appointmentPrice
          ? "Charge full session fee of each session"
          : `Charge full session fee ($${appointmentPrice})`
      }`,
      value: "full_fee",
    },
    {
      id: "no-charge",
      label: "Waive any fee associated with this session",
      value: "no_charge",
    },
  ];

  const dispatch = useDispatch();

  const [cancelReason, setCancelReason] = useState("patient_no_show");
  const [chargeNoShow, setChargeNoShow] = useState(
    "charge_late_cancellation_fee",
  );

  const [error, setError] = useState<any>();
  const [busy, setBusy] = useState(false);

  const handleHide = () => {
    setError(undefined);
    setBusy(false);
    setCancelReason("patient_no_show");
    setChargeNoShow("charge_late_cancellation_fee");
    onHide();
  };

  const onCancelReasonChange = (event) => {
    const inputField = event.currentTarget;
    setCancelReason(inputField.value);
  };

  const onChargeNoShowChange = (event) => {
    const inputField = event.currentTarget;
    setCancelReason("patient_no_show");
    setChargeNoShow(inputField.value);
  };

  const confirmCancelSession = async (id) => {
    setBusy(true);
    try {
      console.log(id, cancelReason, chargeNoShow);
      if (cancelReason === "abandoned") {
        await confirmSessionAttendance(id, "abandoned", null, null);
      } else if (cancelReason === "patient_no_show") {
        await confirmSessionAttendance(
          id,
          "patient_no_show",
          null,
          chargeNoShow,
        );
      }
      handleHide();
      dispatch(closeSidesheet());
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  };

  const confirmCancelAllSessions = () => {
    appointmentIds.forEach(async (id) => {
      try {
        await confirmCancelSession(id);
        dispatch(closeSidesheet());
      } catch (error) {
        setError(error);
      }
    });
  };

  return (
    <ConfirmModal
      labelTitle={"No-show OR Missed?"}
      children={
        <div>
          <p>
            What is the reason for cancellation? Your patient will be charged
            based on the selection below.
          </p>
          <RadioButton
            className={styles["confirm-attendance__radio"]}
            id={CANCEL_REASON_RADIOS[0].value}
            label={CANCEL_REASON_RADIOS[0].label}
            value={CANCEL_REASON_RADIOS[0].value}
            name="cancel-reason"
            onChange={(event) => {
              track(
                amplitudePageName,
                [
                  AMPLITUDE_COMPONENT_NAME,
                  `${CANCEL_REASON_RADIOS[0].id}-radio-button`,
                ],
                "click",
              );
              onCancelReasonChange(event);
              setChargeNoShow("charge_cancellation_fee");
            }}
            checked={cancelReason === CANCEL_REASON_RADIOS[0].value}
          />
          <div className={styles["confirm-attendance__no-show"]}>
            <p className={"rg-14"}>
              Would you like to charge your patient for No-show?
            </p>
            {CHARGE_NO_SHOW_RADIOS.map((radio, index) => {
              return (
                <RadioButton
                  key={"charge-no-show" + index}
                  className={styles["confirm-attendance__radio"]}
                  id={radio.value}
                  label={<label>{radio.label}</label>}
                  value={radio.value}
                  name="charge-no-show"
                  onChange={(e) => {
                    track(
                      amplitudePageName,
                      [
                        AMPLITUDE_COMPONENT_NAME,
                        `${CHARGE_NO_SHOW_RADIOS[index].id}-radio-button`,
                      ],
                      "click",
                    );

                    onChargeNoShowChange(e);
                  }}
                  checked={chargeNoShow === radio.value}
                />
              );
            })}
          </div>

          <RadioButton
            className={styles["confirm-attendance__radio"]}
            id={CANCEL_REASON_RADIOS[1].value}
            label={CANCEL_REASON_RADIOS[1].label}
            value={CANCEL_REASON_RADIOS[1].value}
            name="cancel-reason"
            onChange={(event) => {
              track(
                amplitudePageName,
                [
                  AMPLITUDE_COMPONENT_NAME,
                  `${CANCEL_REASON_RADIOS[1].id}-radio-button`,
                ],
                "click",
              );
              onCancelReasonChange(event);
              setChargeNoShow("");
            }}
            checked={cancelReason === CANCEL_REASON_RADIOS[1].value}
          />
        </div>
      }
      labelConfirm={"Confirm"}
      hideCancel={true}
      show={show}
      onCancel={() => {
        handleHide();
      }}
      onConfirm={() => {
        track(
          amplitudePageName,
          [AMPLITUDE_COMPONENT_NAME, "confirm-button"],
          "click",
        );
        !cancelAll
          ? confirmCancelSession(appointmentIds[0])
          : confirmCancelAllSessions();
      }}
      disabled={busy}
      error={error}
    />
  );
};

export default ConfirmCancelModal;
